import { createApp } from 'vue';

import { loader } from '@guolao/vue-monaco-editor';
import usAllGeo from '@highcharts/map-collection/countries/us/us-all.geo.json';
import * as Sentry from '@sentry/vue';
import Highcharts from 'highcharts';
import HighchartsAnnotations from 'highcharts/modules/annotations';
import HighchartsMapModule from 'highcharts/modules/map';
import HighchartsMore from 'highcharts/highcharts-more';
import HighchartsPatternFill from 'highcharts/modules/pattern-fill';
import HighchartsVue from 'highcharts-vue';
import LogtailPlugin from './plugins/logtail';
import SmoothImagePlugin from './plugins/smooth-image';

import { SENTRY_DSN, INTERCOM_APP_ID, POSTHOG_APP_ID, PRODUCTION, handleGlobalError } from './store/constants';
import * as filters from './filters';
import App from './App.vue';
import eventBus from './event-bus';
import navigationMixin from './mixins/navigation_mixin.js';
import { identifyUser } from './posthog_tracking';
import { initPosthog, persistedDistinctId } from './plugins/posthog';
import router from './router';
import store from './store';
import { hydrateFeatureFlags, refreshFeatureFlagsOnForeground } from './store/feature_flags';
import { applyBrandTheme } from './services/theme';
import { installCalculatorActorHeaders } from './services/calculator_actor';

// Import styles
import './assets/tailwind.css';

loader.config({
    paths: {
        vs: 'https://cdn.jsdelivr.net/npm/monaco-editor@0.54.0/min/vs',
    },
});

const app = createApp(App);

app.config.errorHandler = function (err, vm, info) {
    handleGlobalError(err, vm, info);
};

app.use(store);
installCalculatorActorHeaders(store);
// NOTE: the router is deliberately NOT installed here — vue-router starts the
// initial navigation at app.use(router), and the flag-gated route guards must
// see hydrated feature flags. It installs at the end of the boot chain below.
app.use(SmoothImagePlugin);

// Apply the host partner's custom brand color (v-brand CSS vars) on load and whenever
// the profile changes. The brand profile, never the workspace one: the main app stays
// Valur-branded even for advisors who belong to a firm. No-op (Valur defaults) when no
// brand_color is set.
applyBrandTheme(store.getters['marketing/brand_profile']);
store.watch(
    (state, getters) => getters['marketing/brand_profile'],
    profile => applyBrandTheme(profile),
);

if (PRODUCTION) {
    app.use(LogtailPlugin);
}
// PostHog initializes in the boot chain at the bottom of this file (every env
// with a project key; event capture stays prod/debug-only) so it can bootstrap
// backend-evaluated feature flags before the router runs.

HighchartsAnnotations(Highcharts);
HighchartsMapModule(Highcharts);
HighchartsMore(Highcharts);
// Registered with the others rather than lazily: a chart that draws a `{ pattern }` fill
// before a lazy import resolves paints it plain (the GRAT charts hatch their estate-tax band).
HighchartsPatternFill(Highcharts);
Highcharts.maps['usAllGeo'] = usAllGeo;
app.use(HighchartsVue);

// Make filters available as global properties (Vue 3 removed global filters)
app.config.globalProperties.$filters = filters;

// Make event bus available globally (replaces $root.$emit/$on)
app.config.globalProperties.$eventBus = eventBus;

// Add navigateTo globally
app.mixin(navigationMixin);

// Intercom integration (production only)
if (PRODUCTION && INTERCOM_APP_ID) {
    window.intercomSettings = { app_id: INTERCOM_APP_ID };
    (function () {
        const w = window;
        const ic = w.Intercom;
        if (typeof ic === 'function') {
            ic('reattach_activator');
            ic('update', w.intercomSettings);
        } else {
            const i = function () {
                i.c(arguments);
            };
            i.q = [];
            i.c = function (args) {
                i.q.push(args);
            };
            w.Intercom = i;
            const s = document.createElement('script');
            s.type = 'text/javascript';
            s.async = true;
            s.src = 'https://widget.intercom.io/widget/' + INTERCOM_APP_ID;
            document.head.appendChild(s);
        }
    })();
}

// Production-only analytics scripts
if (PRODUCTION) {
    // Sentry
    Sentry.init({
        app,
        dsn: SENTRY_DSN,
        sendDefaultPii: true,
        // Component props can carry client SSNs (ENG-3499).
        attachProps: false,
        integrations: [Sentry.browserTracingIntegration({ router }), Sentry.replayIntegration()],
        // Drop noisy third-party errors we can't fix from source:
        // - web-vitals `Array.prototype.at()` on pre-Chrome-92 headless bots. Pattern
        //   targets the minified `this.<letter>.at(-1)` shape — stable across bundler
        //   drift and unlikely to collide with our own (non-minified) property names.
        // - PostHog rrweb-record `bufferBelongsToIframe` on cross-origin iframe canvases.
        ignoreErrors: [/this\.[a-z]\.at is not a function/, /bufferBelongsToIframe/],
        // Tracing
        tracesSampleRate: 1.0, // Capture 100% of the transactions
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: ['https://app.valur.com'],
        replaysSessionSampleRate: 1.0,
        replaysOnErrorSampleRate: 1.0,
        enableLogs: true,
    });

    // AHrefs
    const ahrefsScript = document.createElement('script');
    ahrefsScript.src = 'https://analytics.ahrefs.com/analytics.js';
    ahrefsScript.dataset.key = 'PZhhbXapT56a1VWkCZCMSw';
    ahrefsScript.async = true;
    document.head.appendChild(ahrefsScript);

    // Microsoft Clarity
    (function (c, l, a, r, i, t, y) {
        c[a] =
            c[a] ||
            function () {
                (c[a].q = c[a].q || []).push(arguments);
            };
        t = l.createElement(r);
        t.async = 1;
        t.src = 'https://www.clarity.ms/tag/' + i;
        y = l.getElementsByTagName(r)[0];
        y.parentNode.insertBefore(t, y);
    })(window, document, 'clarity', 'script', 'eup1as3lc1');

    // Meta Pixel — base loader (was previously in public/index.html; restored on the
    // Vite migration). Conversion events fire via window.fbq elsewhere.
    (function (f, b, e, v) {
        if (f.fbq) return;
        function n() {
            if (n.callMethod) {
                n.callMethod.apply(n, arguments);
            } else {
                n.queue.push(arguments);
            }
        }
        f.fbq = n;
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = '2.0';
        n.queue = [];
        const t = b.createElement(e);
        t.async = true;
        t.src = v;
        const s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    window.fbq('init', '26274611615559641');
    window.fbq('track', 'PageView');

    // Twitter conversion tracking — base loader. Event pixels fire via
    // window.twq(...) in src/pixel_tracking.js, which needs this loaded first.
    (function (e, t, n) {
        if (e.twq) return;
        const s = function () {
            if (s.exe) {
                s.exe.apply(s, arguments);
            } else {
                s.queue.push(arguments);
            }
        };
        s.version = '1.1';
        s.queue = [];
        e.twq = s;
        const u = t.createElement(n);
        u.async = true;
        u.src = 'https://static.ads-twitter.com/uwt.js';
        const a = t.getElementsByTagName(n)[0];
        a.parentNode.insertBefore(u, a);
    })(window, document, 'script');
    window.twq('config', 'q0maq');

    // LinkedIn Insight Tag
    window._linkedin_partner_id = '8359380';
    window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
    window._linkedin_data_partner_ids.push(window._linkedin_partner_id);
    (function (l) {
        if (!l) {
            window.lintrk = function (a, b) {
                window.lintrk.q.push([a, b]);
            };
            window.lintrk.q = [];
        }
        const s = document.getElementsByTagName('script')[0];
        const b = document.createElement('script');
        b.type = 'text/javascript';
        b.async = true;
        b.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';
        s.parentNode.insertBefore(b, s);
    })(window.lintrk);
}

// Rehydrate the session from the HttpOnly refresh cookie before first paint, so the
// guards see the correct auth state on reload (no logged-in→login flash). Always
// resolves (fast "logged out" when there's no valid cookie), so the app still mounts.
// Feature flags hydrate next — the backend evaluates them under the fresh auth
// cookie — and PostHog initializes with the result bootstrapped, so router guards
// and first paint see the same flag decisions as the server. Every step is
// non-fatal: any failure falls back to the compiled VITE_* flag defaults.
Promise.resolve()
    .then(async () => {
        // Install before requests and route guards: even a held initial navigation
        // must leave the simulator's Reset control available. __DEV_TOOLS__ is false
        // in production builds, so Vite removes this entire branch and the developer
        // tools module graph. Only the dev server and PR previews keep them.
        if (__DEV_TOOLS__) {
            const { installDeveloperTools } = await import('./dev/dev_tools');
            const dispose = installDeveloperTools({ app, router, store });
            import.meta.hot?.dispose(dispose);
        }
    })
    .then(() => store.dispatch('refreshJWT'))
    .then(async () => {
        let bootstrap = null;
        if (POSTHOG_APP_ID) {
            bootstrap = await hydrateFeatureFlags(persistedDistinctId());
            if (bootstrap) {
                bootstrap.isIdentifiedID = store.getters.isLoggedIn;
            }
        }
        if (initPosthog(app, bootstrap)) {
            const user = store.getters.user;
            if (user.id) {
                // updateAuth's identify ran before PostHog init (refreshJWT
                // precedes it) — re-identify the rehydrated session so flag
                // targeting and analytics attach to the logged-in user.
                identifyUser(user.id, {
                    email: user.email,
                    is_advisor: user.is_advisor,
                    first_name: user.first_name,
                    last_name: user.last_name,
                    is_staff: user.is_staff,
                    advisor_role: user.advisor_role,
                });
            }
        }
        // Keep an already-open tab honest: a flag switched off has to reach it
        // too, or the kill switch only works for people who happen to reload.
        // Same POSTHOG_APP_ID guard as the boot hydrate — envs without PostHog
        // stay on their compiled defaults for the whole session.
        if (POSTHOG_APP_ID) {
            refreshFeatureFlagsOnForeground(persistedDistinctId);
        }
    })
    .catch(e => {
        console.error('[boot] feature-flag/PostHog setup failed', e);
    })
    .then(async () => {
        // Install the router only now: vue-router kicks off the initial
        // navigation inside app.use(router), so a hard load of a flag-gated
        // route (documents, advisor tax-season) would otherwise run its guards
        // against the compiled defaults instead of the hydrated values.
        app.use(router);
        try {
            await router.isReady();
        } catch (e) {
            console.error('[boot] initial navigation failed', e);
        }
        app.mount('#app');
    });
